import React from 'react';

const A1 = () => {

    return <>
        <h2>
            Accessibility for LäraNära’s Portal
        </h2>

        <p>LäraNära AB is responsible for this website. We want as many people as possible to be able to use it. This document describes how LäraNära’s portal complies with the law on the accessibility of certain products and services, known accessibility issues, and how you can report issues to us so we can fix them.</p>

        <h3>How accessible is the website?</h3>

        <p>We are aware that parts of the website are not fully accessible. See the section on inaccessible content below for more information.</p>

        <h3>What to do if you can’t use parts of the website?</h3>

        <p>If you need content from LäraNära’s portal that is not accessible to you, but is exempt from the law's scope as described below, you can contact us via:</p>

        <ul>
            <li>The “Feedback” form on the website</li>
            <li>Email: info@laranara.se</li>
            <li>Phone: +46 708 48 95 94 (weekdays 8 AM–4 PM Swedish time)</li>
        </ul>

        <p>We usually respond within one business day.</p>

        <h3>Report accessibility issues</h3>

        <p>We continuously strive to improve website accessibility. If you find problems not mentioned here, or believe we are not meeting legal requirements, let us know.</p>

        <h3>Supervision</h3>

        <p>The Agency for Digital Government in Sweden (Digg) is responsible for monitoring compliance with the law on the accessibility of certain products and services. You can <a href="https://www.digg.se/tdosanmalan">report issues to Digg</a> if you believe our digital service has shortcomings.</p>

        <p>You may also <a href="https://www.digg.se/tdosanmalan">report to Digg</a> if you believe our assessment of disproportionate burden should be reviewed, if our accessibility statement is deficient, or if we did not handle your accessibility request properly.</p>

        <h3>Technical information about this website's accessibility</h3>

        <p>This website is partially compliant with the accessibility law, due to the issues described below.</p>

        <h2>Content not accessible</h2>
        <h3>For users with impaired vision or color blindness</h3>
        <ul>
            <li>Many icons lack descriptive alt texts or aria-labels.</li>
            <li>Several images and icons are missing alt texts.</li>
            <li>Screen readers cannot access status messages.</li>
            <li>Heading structures are missing or incorrect.</li>
            <li>Form fields lack proper labels or are incorrectly linked.</li>
            <li>Tables are missing headers and semantic markup.</li>
            <li>Text and element contrast is often insufficient.</li>
            <li>Focus indication is unclear or missing.</li>
            <li>Keyboard navigation is incomplete.</li>
            <li>Some videos lack captions, audio descriptions, or text alternatives.</li>
            <li>"Skip to content" link is missing.</li>
            <li>Page titles are not unique.</li>
            <li>Focus order is not logical.</li>
        </ul>

        <h3>For users with cognitive disabilities</h3>
        <ul>
            <li>Content lacks clear headings and logical structure.</li>
            <li>Icons and links are not always self-explanatory.</li>
            <li>Links opening in new tabs do not notify the user.</li>
            <li>No visible or accessible help function.</li>
        </ul>

        <h3>For users with motor disabilities</h3>
        <ul>
            <li>Interactive elements are mostly large enough.</li>
            <li>Some features require mouse gestures with no alternative.</li>
        </ul>

        <h3>For users with hearing impairments</h3>
        <ul>
            <li>Some pre-recorded videos lack captions.</li>
            <li>No audio descriptions available for important visual content.</li>
        </ul>

        <h2>Planned improvements</h2>
        <p>We are continuously working to improve accessibility on LäraNära’s portal. The following measures are prioritized:</p>

        <h3>For users with impaired vision or color blindness</h3>
        <ul>
            <li>Add descriptive alt texts and aria-labels to icons and images.</li>
            <li>Ensure correct heading structure on all pages.</li>
            <li>Fix form field labels and associations.</li>
            <li>Add semantic markup to tables.</li>
            <li>Improve color contrast.</li>
            <li>Clarify focus indicators.</li>
            <li>Make all interactive elements keyboard-accessible.</li>
            <li>Ensure status messages are accessible.</li>
        </ul>

        <h3>For users with cognitive disabilities</h3>
        <ul>
            <li>Improve headings and labels.</li>
            <li>Clarify page titles.</li>
            <li>Notify users when links open in new tabs.</li>
            <li>Add links to help and contact.</li>
        </ul>

        <h3>For users with motor disabilities</h3>
        <ul>
            <li>Provide alternatives to mouse gestures.</li>
        </ul>

        <h3>For users with hearing impairments</h3>
        <ul>
            <li>Transcribe and caption all videos.</li>
            <li>Provide audio descriptions or text alternatives for visual content.</li>
        </ul>

        <p>We aim to fix all known accessibility issues by December 2025.</p>

        <h2>How we tested the website</h2>
        <p>Toxic Interactive Solutions AB conducted an independent review of LäraNära’s portal.</p>
        <p>You can find their report here: <a rel="noopener noreferrer"  target='_blank' href="https://static.laranara.se/policies/Tillganglighetsanalys-LaraNara.pdf">Report</a></p>
        <p>The latest evaluation was done on 13 May 2025, based on WCAG 2.2 level AA.</p>
    </>
}

export default A1;